import React, { useCallback, useMemo } from 'react';
import { ArtifactChoice } from '@dreamai/types';
import MapWithCards, { MapItem } from '../canvas/MapWithCards';
import MapOptionCard from '../canvas/MapOptionCard';
import FileLink from '../../shared/FileLink';
import { ReactComponent as TickSelectIcon } from '../../../icons/tick-select.svg';
import { ReactComponent as CloseIcon } from '../../../icons/close-icon.svg';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import './multiple-choice-map-view.scss';

/**
 * Format a numeric price with its ISO 4217 currency code into a display string.
 */
function formatPrice(price: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(price);
  } catch {
    return `${price} ${currencyCode}`;
  }
}

interface MultipleChoiceMapViewProps {
  choices: ArtifactChoice[];
  selectedChoices: string[];
  onChoiceSelect: (choiceId: string) => void;
  readOnly?: boolean;
  /** Height passed through to MapWithCards (default: 600px) */
  height?: number | string;
}

/**
 * Convert an ArtifactChoice into a generic MapItem for the shared MapWithCards.
 */
function choiceToMapItem(choice: ArtifactChoice): MapItem {
  const fields: { label: string; value: string }[] = [];

  // Price field
  if (choice.definitivePrice != null && choice.priceCurrency) {
    fields.push({
      label: 'Price',
      value: formatPrice(choice.definitivePrice, choice.priceCurrency),
    });
  } else if (choice.priceEstimate != null && choice.priceCurrency) {
    fields.push({
      label: 'Price',
      value: `~${formatPrice(choice.priceEstimate, choice.priceCurrency)}`,
    });
  } else if (choice.approxPriceRange && choice.priceCurrency) {
    fields.push({
      label: 'Price',
      value: `${formatPrice(choice.approxPriceRange[0], choice.priceCurrency)} – ${formatPrice(choice.approxPriceRange[1], choice.priceCurrency)}`,
    });
  }

  // Extra fields from the choice
  if (choice.fields) {
    for (const f of choice.fields) {
      fields.push({ label: f.label, value: f.value });
    }
  }

  return {
    id: choice.choiceId,
    title: choice.choiceTitle,
    imageUrl: choice.imageUrl,
    address: choice.address,
    lat: choice.lat,
    lng: choice.lng,
    fields,
  };
}

const MultipleChoiceMapView: React.FC<MultipleChoiceMapViewProps> = ({
  choices,
  selectedChoices,
  onChoiceSelect,
  readOnly = false,
  height,
}) => {
  const [lightboxIndex, setLightboxIndex] = React.useState(-1);

  const mapItems = useMemo(
    () => choices.map((c) => choiceToMapItem(c)),
    [choices],
  );

  const renderCard = useCallback(
    (
      item: MapItem,
      cardProps: {
        isFocused: boolean;
        onMouseEnter: () => void;
        onMouseLeave: () => void;
        onView: () => void;
        cardRef: (el: HTMLDivElement | null) => void;
      },
    ) => {
      const isSelected = selectedChoices.includes(item.id);
      // Show only price or first field on the compact card
      const previewField =
        item.fields.find((f) => f.label === 'Price') || item.fields[0];
      const previewFields = previewField ? [previewField] : [];
      return (
        <div
          key={item.id}
          ref={cardProps.cardRef}
          className={`mc-map-card-wrapper${cardProps.isFocused ? ' card-focus-flash' : ''}${isSelected ? ' selected' : ''}`}
          onMouseEnter={cardProps.onMouseEnter}
          onMouseLeave={cardProps.onMouseLeave}
        >
          <MapOptionCard
            id={item.id}
            title={item.title}
            imageUrl={item.imageUrl}
            address={item.address}
            fields={previewFields}
            onView={cardProps.onView}
            tickSlot={
              !readOnly ? (
                <div
                  className={`mc-map-select-tick${isSelected ? ' selected' : ''}`}
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChoiceSelect(item.id);
                  }}
                >
                  <TickSelectIcon />
                </div>
              ) : undefined
            }
          />
        </div>
      );
    },
    [selectedChoices, readOnly, onChoiceSelect],
  );

  const renderDetailView = useCallback(
    (item: MapItem, onClose: () => void) => {
      const choice = choices.find((c) => c.choiceId === item.id);
      if (!choice) return null;

      const allImages: string[] = [];
      if (choice.imageUrl) allImages.push(choice.imageUrl);
      if (choice.additionalImages) {
        for (const img of choice.additionalImages) {
          if (img && !allImages.includes(img)) allImages.push(img);
        }
      }

      return (
        <div className='detail-view-overlay'>
          <div className='detail-view-panel'>
            <div className='detail-view-header'>
              <div className='combined-header'>
                <h2>{choice.choiceTitle}</h2>
                {choice.address && <small>{choice.address}</small>}
              </div>
              <button className='close-button' onClick={onClose}>
                <CloseIcon />
              </button>
            </div>

            {choice.choiceBlurb && (
              <div className='detail-view-description'>
                {choice.choiceBlurb}
              </div>
            )}

            {item.fields.length > 0 && (
              <div className='mc-map-detail-fields'>
                {item.fields.map((f, idx) => (
                  <div key={idx} className='mc-map-detail-field'>
                    <span className='field-label'>{f.label}</span>
                    <span className='field-value'>{f.value}</span>
                  </div>
                ))}
              </div>
            )}

            {choice.links && choice.links.length > 0 && (
              <div className='mc-map-detail-links'>
                {choice.links.map((link, idx) => (
                  <FileLink key={idx} url={link.url}>
                    {link.label}
                  </FileLink>
                ))}
              </div>
            )}

            {allImages.length > 0 && (
              <>
                <div className='detail-view-gallery'>
                  {allImages.map((url, idx) => (
                    <div
                      key={idx}
                      className='gallery-thumb'
                      onClick={() => setLightboxIndex(idx)}
                    >
                      <img
                        src={url}
                        alt={`${choice.choiceTitle} ${idx + 1}`}
                        referrerPolicy='no-referrer'
                      />
                    </div>
                  ))}
                </div>
                <Lightbox
                  open={lightboxIndex >= 0}
                  close={() => setLightboxIndex(-1)}
                  index={lightboxIndex}
                  slides={allImages.map((url) => ({ src: url }))}
                />
              </>
            )}
          </div>
        </div>
      );
    },
    [choices, lightboxIndex],
  );

  return (
    <MapWithCards
      items={mapItems}
      selectedIds={selectedChoices}
      renderCard={renderCard}
      renderDetailView={renderDetailView}
      className='mc-map-view'
      height={height}
    />
  );
};

export default MultipleChoiceMapView;
